import { useEffect, useState } from "react";
import { Camera, VideoCamera, ChatCircleText, Trash, X, ArrowLeft, ImageSquare, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" }); }
  catch (_e) { return iso?.slice(0, 10); }
}
function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const days = Math.floor(ms / 86_400_000);
    if (days <= 0) return "today";
    if (days === 1) return "1 day ago";
    if (days < 7) return `${days} days ago`;
    if (days < 14) return "1 week ago";
    return `${Math.floor(days / 7)} weeks ago`;
  } catch (_e) { return "—"; }
}

export default function TrainerProgress() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);

  const loadClients = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trainer/progress/clients");
      setClients(data.clients || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadClients(); }, []);

  if (selectedClient) {
    return (
      <ClientEntriesView
        client={selectedClient}
        onBack={() => { setSelectedClient(null); loadClients(); }}
      />
    );
  }

  return (
    <div className="space-y-8" data-testid="trainer-progress-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tracker text-brand mb-3">[ Client Progress ]</div>
          <h1 className="font-display text-4xl sm:text-5xl">Progress library.</h1>
          <p className="text-zinc-500 text-xs mt-2">Review weekly photos, form-check videos, and leave coaching notes.</p>
        </div>
      </div>

      {loading ? (
        <p className="tracker text-zinc-500">Loading…</p>
      ) : clients.length === 0 ? (
        <div className="card-flat p-16 text-center text-zinc-500" data-testid="trainer-progress-empty">
          <Camera size={48} weight="duotone" className="text-zinc-700 mx-auto mb-3" />
          <div className="tracker">No clients yet</div>
        </div>
      ) : (
        <div className="card-flat overflow-x-auto">
          <table className="w-full" data-testid="trainer-progress-clients-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left tracker px-6 py-3 text-zinc-400">Client</th>
                <th className="text-left tracker px-6 py-3 text-zinc-400">Entries</th>
                <th className="text-left tracker px-6 py-3 text-zinc-400">Last upload</th>
                <th className="text-right tracker px-6 py-3 text-zinc-400">Action</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-[#1A1A1A]" data-testid={`trainer-progress-client-row-${c.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{c.name}</span>
                      {c.unread_client_replies > 0 && (
                        <span
                          className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold bg-emerald-500 text-zinc-950 rounded-full"
                          data-testid={`trainer-progress-unread-${c.id}`}
                        >
                          {c.unread_client_replies > 99 ? "99+" : c.unread_client_replies}
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-xs text-zinc-500">{c.email}</div>
                  </td>
                  <td className="px-6 py-4">
                    {c.entry_count > 0 ? (
                      <span className="font-display text-xl text-brand-light">{c.entry_count}</span>
                    ) : (
                      <span className="tracker text-zinc-600">none</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {c.last_upload ? (
                      <div>
                        <div className="text-zinc-300">{formatRelative(c.last_upload)}</div>
                        <div className="tracker text-zinc-500 text-[10px] mt-0.5">{new Date(c.last_upload).toLocaleDateString()}</div>
                      </div>
                    ) : (
                      <span className="tracker text-zinc-600">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      type="button"
                      onClick={() => setSelectedClient(c)}
                      disabled={c.entry_count === 0}
                      className="btn-ghost !py-1.5 !px-3 !text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`trainer-progress-view-${c.id}`}
                    >
                      View entries
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ClientEntriesView({ client, onBack }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEntryId, setOpenEntryId] = useState(null);
  const [openEntry, setOpenEntry] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/progress/entries", { params: { client_id: client.id } });
      setEntries(data.entries || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load entries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client.id]);

  const openEntry_ = async (id) => {
    setOpenEntryId(id);
    setOpenEntry(null);
    try {
      const { data } = await api.get(`/progress/entries/${id}`);
      setOpenEntry(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load entry");
      setOpenEntryId(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="trainer-progress-entries-view">
      <button type="button" onClick={onBack} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="trainer-progress-back-btn">
        <ArrowLeft size={14} weight="bold" /> Back to clients
      </button>
      <div>
        <div className="tracker text-brand mb-3">[ {client.name} ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">{client.name}'s timeline.</h1>
        <p className="text-zinc-500 text-xs mt-2 font-mono">{client.email}</p>
      </div>

      {loading ? (
        <p className="tracker text-zinc-500">Loading…</p>
      ) : entries.length === 0 ? (
        <div className="card-flat p-12 text-center">
          <ImageSquare size={48} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">No entries from this client yet.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="trainer-progress-entries-grid">
          {entries.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => openEntry_(e.id)}
              className="text-left card-flat p-4 hover:border-brand-light/50 transition-colors"
              data-testid={`trainer-progress-entry-card-${e.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="tracker text-zinc-500 text-[10px]">{e.week_label}</span>
                <div className="flex items-center gap-2">
                  {e.unread_client_replies > 0 && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold bg-emerald-500 text-zinc-950 px-1.5 py-0.5 rounded-full" data-testid={`trainer-progress-entry-unread-${e.id}`}>
                      {e.unread_client_replies} NEW
                    </span>
                  )}
                  <span className="text-zinc-600 text-[10px]">{formatDate(e.created_at)}</span>
                </div>
              </div>
              <div className="aspect-[3/2] bg-zinc-900 border border-zinc-800 mb-3 flex items-center justify-center text-zinc-600">
                <div className="text-center">
                  <Camera size={32} weight="duotone" className="mx-auto" />
                  <p className="tracker text-[10px] mt-1">tap to review</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-zinc-400 text-xs">
                <span className="inline-flex items-center gap-1"><Camera size={12} weight="bold" /> {e.photo_count}</span>
                {e.has_video && <span className="inline-flex items-center gap-1"><VideoCamera size={12} weight="bold" /> 1 video</span>}
                {e.comment_count > 0 && <span className="inline-flex items-center gap-1 text-brand-light"><ChatCircleText size={12} weight="bold" /> {e.comment_count}</span>}
              </div>
              {e.notes && <p className="text-zinc-500 text-xs mt-2 line-clamp-2">"{e.notes}"</p>}
            </button>
          ))}
        </div>
      )}

      {openEntryId && (
        <TrainerEntryDetailModal
          entryId={openEntryId}
          entry={openEntry}
          clientName={client.name}
          onClose={() => { setOpenEntryId(null); setOpenEntry(null); load(); }}
          onReload={() => openEntry_(openEntryId)}
        />
      )}
    </div>
  );
}

function TrainerEntryDetailModal({ entryId, entry, clientName, onClose, onReload }) {
  const { user } = useAuth();
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const [busy, setBusy] = useState(false);

  const postComment = async (e) => {
    e?.preventDefault?.();
    const text = commentText.trim();
    if (!text) {
      toast.error("Comment cannot be empty");
      return;
    }
    if (text.length > 1000) {
      toast.error("Comment too long (max 1000 chars)");
      return;
    }
    setPosting(true);
    try {
      await api.post(`/progress/entries/${entryId}/comment`, { text });
      setCommentText("");
      onReload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    setBusy(true);
    try {
      await api.delete(`/progress/entries/${entryId}/comment/${id}`);
      onReload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="trainer-progress-entry-modal">
      <div className="bg-zinc-950 border border-zinc-800 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="p-6 border-b border-zinc-800 flex items-center justify-between gap-4 sticky top-0 bg-zinc-950 z-10">
          <div>
            <p className="tracker text-zinc-500 text-[10px]">{entry?.week_label || "—"} · {clientName}</p>
            <h2 className="font-display text-2xl">{entry ? formatDate(entry.created_at) : "Loading…"}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="trainer-progress-entry-close"><X size={20} weight="bold" /></button>
        </header>

        {!entry ? (
          <div className="p-6 text-center tracker text-zinc-500">Loading entry…</div>
        ) : (
          <div className="p-6 space-y-6">
            {entry.photos?.length > 0 && (
              <div>
                <p className="tracker text-zinc-500 text-[10px] mb-3">[ PHOTOS ]</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {entry.photos.map((src, i) => (
                    <a key={i} href={src} target="_blank" rel="noreferrer" className="block aspect-square border border-zinc-800 bg-zinc-900 overflow-hidden hover:border-brand-light/50" data-testid={`trainer-progress-photo-${i}`}>
                      <img src={src} alt={`Progress ${i + 1}`} className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {entry.video && (
              <div>
                <p className="tracker text-zinc-500 text-[10px] mb-3">[ FORM-CHECK VIDEO ]</p>
                <video src={entry.video} controls className="w-full max-h-[480px] bg-zinc-900 border border-zinc-800" data-testid="trainer-progress-video" />
              </div>
            )}

            {entry.notes && (
              <div>
                <p className="tracker text-zinc-500 text-[10px] mb-2">[ CLIENT NOTES ]</p>
                <p className="text-zinc-300 text-sm whitespace-pre-wrap border-l-2 border-brand-light pl-3" data-testid="trainer-progress-client-notes">{entry.notes}</p>
              </div>
            )}

            {!entry.photos?.length && !entry.video && !entry.notes && (
              <p className="text-zinc-500 text-sm text-center py-8">This entry is empty.</p>
            )}

            {/* Coaching comments */}
            <div className="pt-2 border-t border-zinc-800" data-testid="trainer-progress-comments-section">
              <p className="tracker text-zinc-500 text-[10px] mb-3">[ COACHING COMMENTS ]</p>
              {(entry.comments || []).length === 0 ? (
                <p className="text-zinc-500 text-xs italic mb-4">No comments yet — leave the first note below.</p>
              ) : (
                <ul className="space-y-3 mb-4" data-testid="trainer-progress-comments-list">
                  {entry.comments.map((c) => {
                    const isMine = c.author_id === user?.id;
                    const isClientReply = c.author_role === "client";
                    const canDelete = isMine || user?.is_owner;
                    return (
                      <li key={c.id} className={`bg-zinc-900/60 border-l-2 ${isClientReply ? "border-emerald-500/60" : "border-brand-light"} border-y border-r border-zinc-800 p-3`} data-testid={`trainer-progress-comment-${c.id}`}>
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <span className={`tracker text-[10px] ${isClientReply ? "text-emerald-400" : "text-brand-light"}`}>
                            {isClientReply ? `${c.author_name} (client)` : c.author_name}
                          </span>
                          <span className="tracker text-[10px] text-zinc-600">{formatRelative(c.created_at)}</span>
                        </div>
                        <p className="text-zinc-200 text-sm whitespace-pre-wrap">{c.text}</p>
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => deleteComment(c.id)}
                            disabled={busy}
                            className="mt-2 text-zinc-500 hover:text-red-400 text-[10px] tracker inline-flex items-center gap-1 disabled:opacity-50"
                            data-testid={`trainer-progress-comment-delete-${c.id}`}
                          >
                            <Trash size={10} weight="bold" /> Delete
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              <form onSubmit={postComment} className="space-y-2" data-testid="trainer-progress-comment-form">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  placeholder="Leave a coaching note — form cues, what to focus on next week, encouragement…"
                  className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-brand-light resize-none"
                  data-testid="trainer-progress-comment-input"
                />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-zinc-600">{commentText.length}/1000</span>
                  <button
                    type="submit"
                    disabled={posting || !commentText.trim()}
                    className="btn-primary !py-2 !px-4 !text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="trainer-progress-comment-submit"
                  >
                    <PaperPlaneTilt size={12} weight="fill" /> {posting ? "Posting…" : "Post comment"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
